import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { motion } from "framer-motion";
import { User, Mail, Crown, BarChart3, Calendar, Edit3, Check, CreditCard, Download, Smartphone, ArrowRight } from "lucide-react";

export default function AccountPage() {
  const { user, checkAuth } = useAuth();
  const [account, setAccount] = useState(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchAccount(); }, []);

  const fetchAccount = async () => {
    try {
      const { data } = await API.get("/account");
      setAccount(data);
      setName(data.name || "");
    } catch {}
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.patch("/account", { name });
      setEditing(false);
      fetchAccount();
      checkAuth();
    } catch {} finally { setSaving(false); }
  };

  const planDetails = {
    free: { label: "Free", color: "var(--text-muted)", bg: "var(--surface-warm)" },
    pro: { label: "Pro", color: "var(--rolex-green)", bg: "var(--rolex-green-light)" },
    teams: { label: "Teams", color: "var(--info)", bg: "var(--info-bg)" },
  };
  const pd = planDetails[account?.plan] || planDetails.free;

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-cream)" }}>
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 pt-8 pb-16">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-display text-2xl font-semibold mb-6" style={{ color: "var(--text-primary)" }}>Account Settings</h1>

          {/* Profile Card */}
          <div className="rounded-2xl p-6 mb-5" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-semibold" style={{ background: "var(--rolex-green-light)", color: "var(--rolex-green)" }}>
                {account?.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <div>
                {editing ? (
                  <div className="flex items-center gap-2">
                    <input className="buddy-input py-1.5 text-sm" style={{ width: "200px" }} value={name} onChange={(e) => setName(e.target.value)} data-testid="account-name-input" />
                    <button onClick={handleSave} className="btn-primary py-1.5 px-3 text-xs" disabled={saving} data-testid="account-save-btn"><Check size={14} /></button>
                    <button onClick={() => setEditing(false)} className="btn-secondary py-1.5 px-3 text-xs">Cancel</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold" style={{ color: "var(--text-primary)" }}>{account?.name || "User"}</h2>
                    <button onClick={() => setEditing(true)} className="p-1 rounded hover:bg-[var(--surface-warm)]" data-testid="account-edit-btn"><Edit3 size={14} style={{ color: "var(--text-muted)" }} /></button>
                  </div>
                )}
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>{account?.email}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-xl" style={{ background: "var(--surface-warm)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <Crown size={14} style={{ color: pd.color }} />
                  <span className="label-caps">Current Plan</span>
                </div>
                <span className="text-sm font-bold px-2.5 py-0.5 rounded-full" style={{ background: pd.bg, color: pd.color }}>{pd.label}</span>
              </div>
              <div className="p-4 rounded-xl" style={{ background: "var(--surface-warm)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <BarChart3 size={14} style={{ color: "var(--rolex-green)" }} />
                  <span className="label-caps">Total Rewrites</span>
                </div>
                <p className="text-lg font-bold" style={{ color: "var(--text-primary)" }}>{account?.total_rewrites?.toLocaleString() || 0}</p>
              </div>
            </div>
          </div>

          {/* Billing */}
          <div className="rounded-2xl p-6 mb-5" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}>
            <h3 className="text-base font-semibold mb-4 flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
              <CreditCard size={16} /> Billing & Subscription
            </h3>
            {account?.plan === "free" ? (
              <div>
                <p className="text-sm mb-3" style={{ color: "var(--text-muted)" }}>You're on the Free plan. Upgrade to unlock more rewrites, variations, and premium features.</p>
                <button onClick={() => window.location.href = "/pricing"} className="btn-primary py-2.5 px-6 text-sm" data-testid="account-upgrade-btn">
                  View Plans <ArrowRight size={14} />
                </button>
              </div>
            ) : (
              <div>
                <p className="text-sm mb-3" style={{ color: "var(--text-muted)" }}>
                  You're on the <strong>{pd.label}</strong> plan. Manage your subscription, update payment method, or download invoices.
                </p>
                <button onClick={() => window.location.href = "/pricing"} className="btn-secondary py-2 px-5 text-sm" data-testid="account-manage-billing">
                  Manage Subscription
                </button>
              </div>
            )}
          </div>

          {/* Downloads */}
          <div className="rounded-2xl p-6" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}>
            <h3 className="text-base font-semibold mb-4 flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
              <Download size={16} /> Get Buddy Everywhere
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <motion.a
                href="/downloads"
                className="p-4 rounded-xl flex items-center gap-3 transition-colors"
                style={{ background: "var(--surface-warm)", textDecoration: "none" }}
                whileHover={{ y: -2, boxShadow: "0 4px 12px rgba(0,0,0,0.06)" }}
                data-testid="account-download-extension"
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "var(--rolex-green-light)" }}>
                  <Download size={18} style={{ color: "var(--rolex-green)" }} />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>Browser Extension</p>
                  <p className="text-xs" style={{ color: "var(--text-muted)" }}>Chrome, Edge, Firefox</p>
                </div>
              </motion.a>
              <motion.a
                href="/downloads"
                className="p-4 rounded-xl flex items-center gap-3 transition-colors"
                style={{ background: "var(--surface-warm)", textDecoration: "none" }}
                whileHover={{ y: -2, boxShadow: "0 4px 12px rgba(0,0,0,0.06)" }}
                data-testid="account-download-keyboard"
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "var(--rolex-gold-light)" }}>
                  <Smartphone size={18} style={{ color: "var(--rolex-gold)" }} />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>Mobile Keyboard</p>
                  <p className="text-xs" style={{ color: "var(--text-muted)" }}>iOS & Android</p>
                </div>
              </motion.a>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
